<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useMediaQuery } from '@vueuse/core';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';
import {
  activateMenu,
  deactivateMenu,
} from './conversation/contextMenu/activeMenuRegistry';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
  // Aceleres: nº de filtros selecionados no overlay (AceleresDropdownFilter).
  // Quando > 0, o ícone do botão de filtro fica verde escuro e exibe um badge.
  activeFilterCount: {
    type: Number,
    default: 0,
  },
  isFilterMenuOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'chatTabChange',
  'filterClick',
  'sortChange',
  'closeFilterMenu',
]);

const hiddenScrollerTabKeys = new Set(['concluidos', 'resolved']);
const visibleItems = computed(() =>
  props.items.filter(item => !hiddenScrollerTabKeys.has(item.key))
);
const TAB_ICON_CLASSES = {
  novos: 'i-lucide-message-circle',
  [wootConstants.ASSIGNEE_TYPE.ME]: 'i-lucide-user-check',
  outros: 'i-lucide-users',
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: 'i-lucide-user-round',
};

const tabIconClassFor = item =>
  item.icon || TAB_ICON_CLASSES[item.key] || 'i-lucide-message-circle';

const activeTabIndex = computed(() => {
  return visibleItems.value.findIndex(item => item.key === props.activeTab);
});

const onTabChange = selectedTabIndex => {
  if (selectedTabIndex >= 0 && selectedTabIndex < visibleItems.value.length) {
    const selectedItem = visibleItems.value[selectedTabIndex];
    if (selectedItem.key !== props.activeTab) {
      emit('chatTabChange', selectedItem.key);
    }
  }
};

useKeyboardEvents({
  'Alt+KeyN': {
    action: () => {
      if (!visibleItems.value.length) return;
      const currentIndex = Math.max(activeTabIndex.value, 0);
      const nextIndex = (currentIndex + 1) % visibleItems.value.length;
      onTabChange(nextIndex);
    },
  },
});

/* Aceleres: ordenação como popover local ancorado ao botão de sort.
   Substituiu o slide-panel sortPanel do AceleresDropdownFilter — quando
   aberto a partir daqui o painel cobria a lista inteira; agora o menu
   flutua ao lado do container da conversa, estilo dropdown. */
const store = useStore();
const SORT_OPTIONS = [
  {
    key: 'last_activity_at_desc',
    label: 'Últimas interações',
    icon: 'i-lucide-arrow-down-narrow-wide',
  },
  {
    key: 'last_activity_at_asc',
    label: 'Mais antigos',
    icon: 'i-lucide-clock-arrow-down',
  },
  {
    key: 'created_at_desc',
    label: 'Data de criação recente',
    icon: 'i-lucide-calendar-arrow-down',
  },
  {
    key: 'created_at_asc',
    label: 'Data de criação antiga',
    icon: 'i-lucide-calendar-arrow-down',
  },
];
const currentSort = computed(
  () => store.getters.getChatSortFilter || 'last_activity_at_desc'
);
// Aceleres: o botão de sort espelha o ícone da ordenação atualmente
// selecionada no menu (acl-pill-sort-menu), em vez de um ícone fixo.
const currentSortOption = computed(
  () =>
    SORT_OPTIONS.find(opt => opt.key === currentSort.value) || SORT_OPTIONS[0]
);
const filterLabel = 'Filtrar';
const sortLabel = 'Ordenar';
const sortConversationsLabel = 'Ordenar conversas';
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const route = useRoute();
const isRouteLeaving = ref(false);

const sortBtnRef = ref(null);
const tabsScrollerRef = ref(null);
const showSortPopover = ref(false);
const popoverStyle = ref({});
const isDraggingTabs = ref(false);
const isPointerDownOnTabs = ref(false);
const hasDraggedTabs = ref(false);
const dragStartX = ref(0);
const dragStartScrollLeft = ref(0);
const DRAG_THRESHOLD = 6;
const canScrollTabsLeft = ref(false);
const canScrollTabsRight = ref(false);

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function computePopoverStyle() {
  const btn = sortBtnRef.value;
  const rect = btn?.getBoundingClientRect?.();

  const gap = 8;
  const menu = document.getElementById('acl-pill-sort-popover');
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = 12;
  const preferredWidth = Math.max(menu?.offsetWidth || 0, 420);
  const width = Math.min(preferredWidth, viewportWidth - margin * 2);
  const menuHeight = Math.min(
    menu?.offsetHeight || 254,
    viewportHeight - margin * 2
  );
  if (!rect) {
    return {
      top: `${margin}px`,
      left: `${margin}px`,
      width: `${width}px`,
    };
  }

  const spaceRight = viewportWidth - rect.right - gap - margin;
  const left =
    spaceRight >= width
      ? rect.right + gap
      : clamp(rect.left - width - gap, margin, viewportWidth - width - margin);
  const spaceBelow = viewportHeight - rect.bottom - gap - margin;
  const spaceAbove = rect.top - gap - margin;
  const top =
    spaceBelow < menuHeight && spaceAbove > spaceBelow
      ? Math.max(margin, rect.top - gap - menuHeight)
      : Math.min(rect.bottom + gap, viewportHeight - margin - menuHeight);

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
}

// Aceleres 2026-09-02: abrir um menu fecha o que estiver aberto. O registro
// (`activeMenuRegistry`) ja era o dono dessa regra para o menu do cartao de
// conversa e o de acoes; ordenar e filtros passaram a entrar nele tambem, para
// a regra valer entre menus de componentes diferentes — que nao se conhecem e
// por isso nunca se fechariam sozinhos.
const sortMenuOwner = Symbol('acl-sort-popover');

function closeSortPopover() {
  deactivateMenu(sortMenuOwner);
  showSortPopover.value = false;
}

async function openSortPopover() {
  activateMenu({ owner: sortMenuOwner, close: closeSortPopover });
  popoverStyle.value = computePopoverStyle();
  showSortPopover.value = true;
  await nextTick();
  popoverStyle.value = computePopoverStyle();
}
const onFilterClick = event => {
  closeSortPopover();
  emit('filterClick', event.currentTarget);
};
async function onSortClick() {
  if (showSortPopover.value) {
    closeSortPopover();
    return;
  }

  emit('closeFilterMenu');
  await nextTick();
  openSortPopover();
}
function selectSort(key) {
  if (key !== currentSort.value) {
    emit('sortChange', key);
  }
  closeSortPopover();
}

// Reposiciona em scroll/resize enquanto aberto; clique fora e Esc fecham.
function onDocClick(e) {
  if (!showSortPopover.value) return;
  const target = e.target;
  if (sortBtnRef.value && sortBtnRef.value.contains(target)) return;
  const menu = document.getElementById('acl-pill-sort-popover');
  if (menu && menu.contains(target)) return;
  closeSortPopover();
}
function onDocKey(e) {
  if (e.key === 'Escape' && showSortPopover.value) closeSortPopover();
}

function updateTabsScrollState() {
  const scroller = tabsScrollerRef.value;
  if (!scroller) {
    canScrollTabsLeft.value = false;
    canScrollTabsRight.value = false;
    return;
  }

  const maxScrollLeft = Math.max(
    0,
    scroller.scrollWidth - scroller.clientWidth
  );
  canScrollTabsLeft.value = scroller.scrollLeft > 2;
  canScrollTabsRight.value = scroller.scrollLeft < maxScrollLeft - 2;
}

function onViewportChange() {
  if (showSortPopover.value) {
    popoverStyle.value = computePopoverStyle();
  }
  updateTabsScrollState();
}

function onTabsWheel(event) {
  const scroller = tabsScrollerRef.value;
  if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

  const delta =
    Math.abs(event.deltaY) >= Math.abs(event.deltaX)
      ? event.deltaY
      : event.deltaX;
  if (!delta) return;

  scroller.scrollLeft += delta;
  event.preventDefault();
  updateTabsScrollState();
}

function startTabsDrag(event) {
  const scroller = tabsScrollerRef.value;
  if (!scroller) return;

  isPointerDownOnTabs.value = true;
  isDraggingTabs.value = false;
  hasDraggedTabs.value = false;
  dragStartX.value = event.clientX;
  dragStartScrollLeft.value = scroller.scrollLeft;
}

function onTabsDrag(event) {
  const scroller = tabsScrollerRef.value;
  if (!scroller || !isPointerDownOnTabs.value) return;

  // Aceleres 2026-08-25: `scrollLeft` e LAYOUT, o delta do rato e VISUAL — sem
  // converter, as abas corriam 1.25x mais que o dedo/cursor. O limiar de
  // arrasto fica em visual de proposito: e uma distancia percebida no ecra.
  // Ver helper/dashboardZoom.js.
  const visualDeltaX = event.clientX - dragStartX.value;
  if (!isDraggingTabs.value && Math.abs(visualDeltaX) < DRAG_THRESHOLD) return;
  const deltaX = visualToLayout(visualDeltaX);

  if (!isDraggingTabs.value) {
    isDraggingTabs.value = true;
    hasDraggedTabs.value = true;
    scroller.setPointerCapture?.(event.pointerId);
  }

  scroller.scrollLeft = dragStartScrollLeft.value - deltaX;
  updateTabsScrollState();
}

function stopTabsDrag(event) {
  if (!isPointerDownOnTabs.value) return;
  isPointerDownOnTabs.value = false;
  if (isDraggingTabs.value) {
    isDraggingTabs.value = false;
    tabsScrollerRef.value?.releasePointerCapture?.(event.pointerId);
  }
  window.setTimeout(() => {
    hasDraggedTabs.value = false;
  }, 0);
}

function onTabClick(index, event) {
  if (hasDraggedTabs.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  onTabChange(index);
}

watch(
  visibleItems,
  () => {
    window.requestAnimationFrame(updateTabsScrollState);
  },
  { deep: true }
);

watch(
  () => props.isFilterMenuOpen,
  isOpen => {
    if (isOpen) closeSortPopover();
  }
);

onMounted(() => {
  document.addEventListener('click', onDocClick, true);
  document.addEventListener('keydown', onDocKey);
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, true);
  updateTabsScrollState();
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true);
  document.removeEventListener('keydown', onDocKey);
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange, true);
});

onBeforeRouteLeave((_to, _from, next) => {
  isRouteLeaving.value = true;
  nextTick(next);
});

// Aceleres: isRouteLeaving e transitorio — esconde os botoes flutuantes
// SOMENTE enquanto a navegacao acontece (no mobile eles ficavam por cima do
// menu lateral e engoliam o toque). Precisa voltar a false quando a rota
// assenta: todas as rotas de conversa compartilham o mesmo ConversationView,
// entao abrir um chat NAO desmonta este componente e a flag ficava ligada
// para sempre — o v-if apagava "Filtrar" e "Ordenar" pelo resto da sessao.
watch(
  () => route.fullPath,
  () => {
    nextTick(() => {
      isRouteLeaving.value = false;
    });
  }
);
</script>

<template>
  <div class="acl-pill-tabs">
    <div class="acl-pill-tabs__rail">
      <div
        v-if="canScrollTabsLeft"
        class="acl-pill-tabs__fade acl-pill-tabs__fade--left"
      />
      <div
        ref="tabsScrollerRef"
        class="acl-pill-tabs__scroller !ps-0 md:[.acl-pill-tabs_&]:!items-center md:[.acl-pill-tabs_&]:!py-[7px]"
        :class="{ 'acl-pill-tabs__scroller--dragging': isDraggingTabs }"
        @pointerdown="startTabsDrag"
        @pointermove="onTabsDrag"
        @pointerup="stopTabsDrag"
        @pointercancel="stopTabsDrag"
        @lostpointercapture="stopTabsDrag"
        @scroll="updateTabsScrollState"
        @wheel="onTabsWheel"
      >
        <button
          v-for="(item, index) in visibleItems"
          :key="item.key"
          type="button"
          class="acl-pill-tab"
          :class="{ 'acl-pill-tab--active': item.key === activeTab }"
          @click="onTabClick(index, $event)"
        >
          <span class="acl-pill-tab__icon-pill" aria-hidden="true">
            <span class="acl-pill-tab__icon" :class="tabIconClassFor(item)" />
          </span>
          <span class="acl-pill-tab__label">{{ item.name }}</span>
          <span
            v-if="
              item.count !== undefined &&
              item.count !== null &&
              Number(item.count) > 0
            "
            v-tooltip.bottom="item.countHint"
            class="acl-pill-tab__count"
          >
            {{ item.count }}
          </span>
        </button>
      </div>
      <div
        v-if="canScrollTabsRight"
        class="acl-pill-tabs__fade acl-pill-tabs__fade--right"
      />
    </div>
    <Teleport
      to="#acl-mobile-conversation-actions"
      :disabled="!isMobileViewport"
      defer
    >
      <div
        v-if="!isRouteLeaving"
        class="acl-pill-actions"
        :class="{ 'acl-pill-actions--mobile': isMobileViewport }"
      >
        <button
          v-tooltip.bottom-end="filterLabel"
          type="button"
          class="acl-pill-action-btn"
          :class="{
            'acl-pill-action-btn--has-filters': activeFilterCount > 0,
            'acl-pill-action-btn--active': isFilterMenuOpen,
          }"
          :aria-label="filterLabel"
          @click.stop="onFilterClick"
        >
          <span class="i-lucide-sliders-horizontal" />
          <span v-if="activeFilterCount > 0" class="acl-pill-filter-badge">{{
            activeFilterCount
          }}</span>
        </button>
        <button
          ref="sortBtnRef"
          v-tooltip.bottom-end="sortLabel"
          type="button"
          class="acl-pill-action-btn"
          :class="{ 'acl-pill-action-btn--active': showSortPopover }"
          :aria-label="sortConversationsLabel"
          :aria-expanded="showSortPopover"
          aria-haspopup="menu"
          @click.stop="onSortClick"
        >
          <span :class="currentSortOption.icon" />
        </button>
      </div>
    </Teleport>

    <Teleport to="body">
      <Transition
        enter-active-class="acl-pill-sort-pop-enter-active"
        enter-from-class="acl-pill-sort-pop-from"
        enter-to-class="acl-pill-sort-pop-to"
        leave-active-class="acl-pill-sort-pop-leave-active"
        leave-from-class="acl-pill-sort-pop-to"
        leave-to-class="acl-pill-sort-pop-from"
      >
        <div
          v-if="showSortPopover"
          id="acl-pill-sort-popover"
          class="acl-pill-sort-menu acl-pill-sort-menu--popover"
          role="menu"
          :aria-label="sortConversationsLabel"
          :style="popoverStyle"
        >
          <button
            v-for="opt in SORT_OPTIONS"
            :key="opt.key"
            type="button"
            role="menuitemradio"
            :aria-checked="opt.key === currentSort"
            class="acl-pill-sort-item"
            :class="{ 'acl-pill-sort-item--active': opt.key === currentSort }"
            @click="selectSort(opt.key)"
          >
            <span class="acl-pill-sort-item__icon" :class="[opt.icon]" />
            <span class="acl-pill-sort-item__label">{{ opt.label }}</span>
            <span
              v-if="opt.key === currentSort"
              class="i-lucide-check acl-pill-sort-item__check"
            />
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.acl-pill-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 0 10px;
  border-bottom: none !important;
  min-width: 0;
}
.acl-pill-tabs__rail {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}
.acl-pill-tabs__scroller {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  scroll-behavior: smooth;
  padding-inline: 12px;
  cursor: grab;
  user-select: none;
  touch-action: pan-y;
}
.acl-pill-tabs__scroller::-webkit-scrollbar {
  display: none;
}
.acl-pill-tabs__scroller--dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}
.acl-pill-tabs__fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 18px;
  pointer-events: none;
  z-index: 1;
}
.acl-pill-tabs__fade--left {
  left: 0;
  background: linear-gradient(to right, #ffffff 20%, rgba(255, 255, 255, 0));
}
.acl-pill-tabs__fade--right {
  right: 0;
  background: linear-gradient(to left, #ffffff 20%, rgba(255, 255, 255, 0));
}

.acl-pill-tab {
  display: inline-flex;
  position: relative;
  align-items: center;
  gap: 6px;
  padding: 11px 12px;
  background: transparent;
  border: 1px solid #d4d4d4;
  border-radius: 50px;
  color: #767676;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.12s,
    color 0.12s,
    border-color 0.12s;
  line-height: 1.2;
  flex-shrink: 0;
  animation: none;
}
.acl-pill-tab:hover {
  color: #525252;
  border-color: #b5b5b5;
}

.acl-pill-tab--active,
.acl-pill-tab--active:hover {
  background: #d9fdd3;
  color: #15603e;
  border-color: #15603e;
}

.acl-pill-tab__label,
.acl-pill-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.acl-pill-tab__icon-pill,
.acl-pill-tab__icon {
  display: none;
}
.acl-pill-tab__count {
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  background: transparent;
  color: inherit;
}
.acl-pill-tab--active .acl-pill-tab__count {
  background: transparent;
  color: #15603e;
}

/* Dark mode */
:global(.dark) .acl-pill-tab {
  border-color: #3f3f46;
  color: #a1a1aa;
}
:global(.dark) .acl-pill-tab:hover {
  border-color: #52525b;
  color: #d4d4d8;
}
:global(.dark) .acl-pill-tab--active,
:global(.dark) .acl-pill-tab--active:hover {
  background: rgba(21, 96, 62, 0.25);
  color: #86efac;
  border-color: #15603e;
}
:global(.dark) .acl-pill-tab__count {
  background: transparent;
}
:global(.dark) .acl-pill-tab--active .acl-pill-tab__count {
  background: transparent;
  color: #86efac;
}
:global(.dark) .acl-pill-tabs__fade--left {
  background: linear-gradient(to right, #111315 20%, rgba(17, 19, 21, 0));
}
:global(.dark) .acl-pill-tabs__fade--right {
  background: linear-gradient(to left, #111315 20%, rgba(17, 19, 21, 0));
}

/* Aceleres: ações à direita do container .acl-pill-tabs (filter + sort) */
.acl-pill-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  padding-inline-start: 6px;
  background: #ffffff;
}
.acl-pill-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: #525252;
  cursor: pointer;
  position: relative;
  transition:
    background-color 0.12s,
    color 0.12s;
}
.acl-pill-action-btn:hover {
  background-color: rgba(10, 10, 10, 0.06);
  color: #0a0a0a;
}
.acl-pill-action-btn--active,
.acl-pill-action-btn--active:hover {
  background-color: rgba(21, 96, 62, 0.12);
  color: #15603e;
}
/* Aceleres: filtros ativos — ícone verde escuro + badge com a contagem. */
.acl-pill-action-btn--has-filters,
.acl-pill-action-btn--has-filters:hover {
  color: #15603e;
}
.acl-pill-filter-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #15603e;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  border-radius: 50px;
  border: none; /* sem borda branca (pedido 2026-07-02) */
  pointer-events: none;
}
/* Acl-pill-filter-badge fica de fora (tem sizing/posicao proprias). */
.acl-pill-action-btn > span:not(.acl-pill-filter-badge) {
  width: 18px;
  height: 18px;
  background-color: currentColor;
}
/* Aceleres 2026-09-01: o no raiz passou a ter so as suas duas classes. As
   utilitarias Tailwind que estavam no template (fixed, z-[9999], h-[254px],
   max-h, overflow-y-auto, overscroll-contain, rounded, border, bg, shadow)
   viraram declaracoes aqui — a maioria ja estava duplicada nesta regra. */
.acl-pill-sort-menu {
  position: fixed;
  width: 420px;
  min-width: 0;
  height: 254px;
  max-height: calc(100vh - 1.5rem);
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  z-index: 9999;
  padding: 12px;
  box-sizing: border-box;
}
.acl-pill-sort-menu--popover {
  max-width: calc(100vw - 32px);
}
.acl-pill-sort-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #0a0a0a;
  cursor: pointer;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;
}
.acl-pill-sort-item:hover {
  background-color: #f8fafc;
  border-color: #d7dce2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
}
.acl-pill-sort-item--active {
  background-color: #f3faf5;
  border-color: #b8d8c3;
  color: #1daa61;
}
/* Ativo: icone e rotulo assumem o mesmo verde do visto. O rotulo herda o
   `color` do botao; o icone tem cor propria e precisa do override.
   Quem decide de facto e o `public/aceleres-overrides.css` (seletor com id,
   `!important`); estas regras existem para o componente nao mentir. */
.acl-pill-sort-item--active .acl-pill-sort-item__icon,
.acl-pill-sort-item--active .acl-pill-sort-item__label {
  color: #1daa61;
}
.acl-pill-sort-item__icon {
  width: 20px;
  height: 20px;
  color: #0a0a0a;
  background-color: currentColor;
  flex-shrink: 0;
}
.acl-pill-sort-item__label {
  flex: 1;
  white-space: nowrap;
}
.acl-pill-sort-item__check {
  width: 18px;
  height: 18px;
  color: #15603e;
  background-color: currentColor;
  flex-shrink: 0;
  margin-left: auto;
}

/* Animação curta de entrada/saída pra dar peso de popover */
.acl-pill-sort-pop-enter-active,
.acl-pill-sort-pop-leave-active {
  transition:
    opacity 140ms ease-out,
    transform 140ms ease-out;
  transform-origin: top left;
  will-change: opacity, transform;
}
.acl-pill-sort-pop-from {
  opacity: 0;
  transform: scale(0.96) translateX(-4px);
}
.acl-pill-sort-pop-to {
  opacity: 1;
  transform: scale(1) translateX(0);
}

:global(.dark) .acl-pill-action-btn {
  color: #a1a1aa;
}
:global(.dark) .acl-pill-action-btn:hover {
  background-color: rgba(229, 229, 229, 0.08);
  color: #e5e5e5;
}
:global(.dark) .acl-pill-action-btn--active,
:global(.dark) .acl-pill-action-btn--active:hover {
  background-color: rgba(21, 96, 62, 0.25);
  color: #86efac;
}
:global(.dark) .acl-pill-action-btn--has-filters,
:global(.dark) .acl-pill-action-btn--has-filters:hover {
  color: #86efac;
}
:global(.dark) .acl-pill-filter-badge {
  background: #15603e;
}
:global(.dark) .acl-pill-actions {
  background: #111315;
}
:global(.dark) .acl-pill-sort-menu {
  background: #1f2123;
  border-color: #2c2f33;
}
:global(.dark) .acl-pill-sort-item {
  background: #202326;
  border-color: #34383d;
  color: #0a0a0a;
}
:global(.dark) .acl-pill-sort-item:hover {
  background-color: #292d31;
  border-color: #454a50;
}
:global(.dark) .acl-pill-sort-item--active {
  background-color: rgba(21, 96, 62, 0.28);
  border-color: #397554;
  color: #1daa61;
}
:global(.dark) .acl-pill-sort-item--active .acl-pill-sort-item__icon,
:global(.dark) .acl-pill-sort-item--active .acl-pill-sort-item__check {
  color: #1daa61;
}

.acl-pill-sort-menu .acl-pill-sort-item,
.acl-pill-sort-menu .acl-pill-sort-item__icon,
.acl-pill-sort-menu .acl-pill-sort-item__label {
  color: #0a0a0a !important;
}
/* O escolhido escapa do cinzento acima — icone, rotulo e visto no mesmo verde. */
.acl-pill-sort-menu .acl-pill-sort-item--active,
.acl-pill-sort-menu .acl-pill-sort-item--active .acl-pill-sort-item__icon,
.acl-pill-sort-menu .acl-pill-sort-item--active .acl-pill-sort-item__label {
  color: #1daa61 !important;
}

@keyframes acl-pill-tab-fade-in {
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 767px) {
  .acl-pill-tabs {
    position: fixed;
    inset-inline: 6px;
    bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    z-index: 1000;
    width: auto;
    min-height: 76px;
    gap: 0;
    padding: 4px 2px;
    overflow: hidden;
    border: 1px solid rgba(148, 163, 184, 0.22) !important;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 7px 24px rgba(15, 23, 42, 0.14);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
  }

  .acl-pill-tabs__rail {
    flex: 1 1 auto;
    width: 100%;
    overflow: visible;
  }

  .acl-pill-tabs__scroller {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    width: 100%;
    gap: 2px;
    overflow: visible;
    padding-inline: 0;
    cursor: default;
    touch-action: manipulation;
  }

  .acl-pill-tabs__fade,
  .acl-pill-actions:not(.acl-pill-actions--mobile) {
    display: none;
  }

  .acl-pill-tab {
    position: relative;
    width: 100%;
    min-width: 0;
    height: 66px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 3px 2px;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #54656f;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    animation: none;
  }

  .acl-pill-tab:hover {
    border-color: transparent;
    color: #111b21;
  }

  .acl-pill-tab--active,
  .acl-pill-tab--active:hover {
    background: transparent;
    border-color: transparent;
    color: #0b8457;
  }

  .acl-pill-tab__icon-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 38px;
    border-radius: 999px;
    color: inherit;
    background-color: transparent;
  }

  /* Aceleres 2026-09-09: a caixa da aba activa. So no telemovel — no
     computador a aba activa continua a distinguir-se pela pilula inteira, que
     ja tem fundo proprio (ver o bloco `.acl-pill-tab--active` la em cima).
     O verde e o do proprio texto activo a 14%, e nao uma cor nova.
     Existe um gemeo `!important` em `public/aceleres-overrides.css`
     (`.painel-atendimento .acl-pill-tab--active .acl-pill-tab__icon-pill`) e e
     ELE que pinta a barra; os dois ficam iguais de proposito. */
  .acl-pill-tab--active .acl-pill-tab__icon-pill {
    background-color: rgba(11, 132, 87, 0.14);
  }

  :global(.dark) .acl-pill-tab--active .acl-pill-tab__icon-pill {
    background-color: rgba(37, 211, 102, 0.18);
  }

  .acl-pill-tab__icon {
    display: inline-block;
    width: 24px;
    height: 24px;
    background-color: currentColor;
    flex-shrink: 0;
  }

  .acl-pill-tab__label {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    font-size: 12px;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .acl-pill-tab__count {
    position: absolute;
    top: 0;
    left: calc(50% + 9px);
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: #25d366;
    color: #ffffff;
    font-size: 9px;
    font-weight: 800;
    line-height: 16px;
  }

  .acl-pill-actions--mobile {
    gap: 10px;
    padding: 0;
    background: transparent;
  }

  .acl-pill-actions--mobile .acl-pill-action-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    box-shadow: none;
    color: #52606d;
  }

  .acl-pill-actions--mobile .acl-pill-action-btn:hover {
    background: rgba(15, 23, 42, 0.05);
  }

  .acl-pill-tab__count--empty {
    display: inline-flex;
    opacity: 0.65;
  }

  .acl-pill-tab--active .acl-pill-tab__count {
    background: #0b8457;
    color: #ffffff;
  }

  :global(.dark) .acl-pill-tabs {
    background: rgba(17, 27, 33, 0.88);
    border-color: rgba(134, 150, 160, 0.26) !important;
    box-shadow: 0 7px 24px rgba(0, 0, 0, 0.32);
  }

  :global(.dark) .acl-pill-tab {
    border-color: transparent;
    color: #8696a0;
  }

  :global(.dark) .acl-pill-tab:hover {
    border-color: transparent;
    color: #e9edef;
  }

  :global(.dark) .acl-pill-tab--active,
  :global(.dark) .acl-pill-tab--active:hover {
    background: transparent;
    border-color: transparent;
    color: #25d366;
  }

  :global(.dark) .acl-pill-tab__count {
    background: #25d366;
    color: #0b141a;
  }
}
</style>
